import Skeleton from "@mui/material/Skeleton";
import styled from "styled-components";

import theme from "../../../styles/theme";
import { NoDataMessage } from "../../../components/Common/TableStyles";
import TransparentButton from "../../../components/Common/Buttons";
import {
  Card,
  Ellipsis,
  PositionCell,
  TeamDot,
  isTeamKind,
  skeletonSx,
  skeletonWidth,
} from "../styles";

const Tile = styled(Card)`
  padding: 14px 14px 8px;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const Title = styled.span`
  font-size: 15px;
  font-weight: 700;
`;

const Unit = styled.span`
  font-family: "Roboto Condensed", sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${theme.colors.grey};
  white-space: nowrap;
`;

const Rows = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
`;

const Cell = styled.td`
  height: 34px;
  border-bottom: 2px solid ${theme.colors.secondaryDark};
  padding: 0;
  vertical-align: middle;
`;

const HolderCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 0 10px;
`;

const Row = styled.tr`
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  transition: background-color 0.12s ease;

  &:hover {
    background-color: ${({ $clickable }) =>
      $clickable ? theme.colors.secondaryDark : "transparent"};
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.gold};
    outline-offset: -2px;
  }
`;

// The driver name keeps its full width and the team name absorbs all the shrinking — a long team
// used to eat into the name next to it. `max-width` still caps the name at the row width so a
// pathologically long name ellipsises instead of overflowing the tile.
const HolderName = styled(Ellipsis)`
  font-size: 13px;
  flex: 0 0 auto;
  max-width: 100%;
`;

const HolderTeam = styled(Ellipsis)`
  font-size: 10px;
  color: ${theme.colors.grey};
  flex: 0 1 auto;
  min-width: 0;
`;

const Value = styled.span`
  font-size: 15px;
  font-weight: 700;
  padding-right: 6px;
`;

const Position = styled(PositionCell)`
  font-family: inherit;
`;

export function BoardTile({ board, onOpenFull, onSelectDriver }) {
  const rows = board.rows || [];
  const isTeamBoard = isTeamKind(board.kind);

  return (
    <Tile>
      <Header>
        <HeaderText>
          <Title>{board.title}</Title>
        </HeaderText>
        <Unit>{board.unit}</Unit>
      </Header>

      {rows.length === 0 ? (
        <NoDataMessage>Ще немає результатів</NoDataMessage>
      ) : (
        <Rows role="table">
          <tbody>
            {rows.map((row) => (
              <Row
                key={`${row.position}-${row.holderId}`}
                $clickable={!isTeamBoard}
                onClick={isTeamBoard ? undefined : () => onSelectDriver(row.holderId)}
                tabIndex={isTeamBoard ? undefined : 0}
                aria-label={isTeamBoard ? undefined : `${row.holderName} — профіль пілота`}
                onKeyDown={
                  isTeamBoard
                    ? undefined
                    : (event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          onSelectDriver(row.holderId);
                        }
                      }
                }
              >
                <Cell style={{ width: 26, paddingLeft: 6 }}>
                  <Position $position={row.position}>{row.position}</Position>
                </Cell>
                <Cell>
                  <HolderCell>
                    {row.teamColor && (
                      <TeamDot $color={row.teamColor} $size={isTeamBoard ? 10 : 8} />
                    )}
                    <HolderName>{row.holderName}</HolderName>
                    {!isTeamBoard && row.teamName && <HolderTeam>{row.teamName}</HolderTeam>}
                  </HolderCell>
                </Cell>
                <Cell style={{ width: 64, textAlign: "right" }}>
                  <Value>{row.value}</Value>
                </Cell>
              </Row>
            ))}
          </tbody>
        </Rows>
      )}

      <TransparentButton
        as="button"
        onClick={() => onOpenFull(board.kind)}
        style={{
          marginTop: 8,
          fontSize: 13,
          padding: "10px 12px",
          border: "none",
          background: "transparent",
          justifyContent: "flex-start",
          width: "100%",
        }}
      >
        Повна таблиця →
      </TransparentButton>
    </Tile>
  );
}

// Ten rows is what a loaded tile shows — the backend's slick.records.depth default. The footer bar
// stands in for the "Повна таблиця" button so the skeleton tile is the same height as the real
// one and the grid doesn't jump when the data lands.
const SKELETON_ROWS = 10;

export function BoardTileSkeleton() {
  return (
    <Tile>
      <Header>
        <HeaderText>
          <Skeleton variant="text" width={128} height={20} sx={skeletonSx} />
        </HeaderText>
        <Skeleton variant="text" width={56} height={13} sx={skeletonSx} />
      </Header>

      <Rows>
        <tbody>
          {Array.from({ length: SKELETON_ROWS }, (_, index) => (
            <tr key={index}>
              <Cell style={{ width: 26, paddingLeft: 6 }}>
                <Skeleton variant="text" width={18} height={17} sx={skeletonSx} />
              </Cell>
              <Cell>
                <HolderCell>
                  <Skeleton
                    variant="text"
                    width={skeletonWidth(index)}
                    height={17}
                    sx={skeletonSx}
                  />
                </HolderCell>
              </Cell>
              <Cell style={{ width: 64 }}>
                <Skeleton
                  variant="text"
                  width={40}
                  height={19}
                  sx={{ ...skeletonSx, marginLeft: "auto", marginRight: "6px" }}
                />
              </Cell>
            </tr>
          ))}
        </tbody>
      </Rows>

      <Skeleton
        variant="text"
        width={168}
        height={19}
        sx={{ ...skeletonSx, margin: "18px 12px 10px" }}
      />
    </Tile>
  );
}
